import { ObjectiveFollowup, ObjectiveStatus, useObjectives, type Objective } from './ObjectiveFollowup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardCopy, Check, ChevronLeft, ChevronRight, FlipHorizontal2, ChevronDown } from 'lucide-react';
import { api } from '../api/client';
import type { SessionParticipant, SessionDetail, CardCatalog, ScoreLetter, Pick, Horizon } from '../types';
import { SCORE_DIMENSIONS, HORIZONS, HORIZON_LABELS, type ScoreDimension, type DimLang } from '../../shared/scoreDimensions';
import { CardTable } from './CardTable';
import { ScoreScale } from './ScoreScale';
import { RichNotes } from './RichNotes';
import { EvolutionTable, type MatrixSession, type TrendScores } from './EvolutionTable';
import { buildParticipantReport, copyText } from '../lib/report';
import { htmlToText, notesHaveContent } from '../lib/html';
import { useT } from '../i18n';

interface HistorySession {
  session_id: number;
  scheduled_date: string;
  title: string | null;
  location: string | null;
  presence: string;
  context_notes: string | null;
  defi: string | null;
  takes_cards: boolean | null;
  animator_name?: string | null;
  updated_by_name?: string | null;
  scores: TrendScores;
  picks: Pick[];
}

interface SynthesisState {
  status: 'ready' | 'pending' | 'disabled' | 'empty' | 'error';
  syntheses: Record<number, string>;
}

interface Objectives {
  measure_id: number | null;
  measure_start: string | null;
  measure_end: string | null;
  goals: string[];
  progression: {
    progression_type: string | null;
    start_pct: number | null;
    target_pct: number | null;
    period_start: string | null;
    period_end: string | null;
    target_date: string | null;
    raw_phrase: string | null;
  } | null;
  /** Formulaire Premier entretien (texts may contain HTML). */
  goals_in_words: string | null;
  informations_complementaires: string | null;
  intake_date: string | null;
  bsp_date: string | null;
}

interface Props {
  session: SessionDetail['session'];
  participant: SessionParticipant;
  catalog: CardCatalog;
  /** Field-scoped: only the changed fields, merged onto the LIVE row upstream.
   *  Never a full participant — a debounced save's closure may be stale, and a
   *  full-row echo used to revert scores/picks set in the meantime. */
  onUpdated: (id: number, patch: Partial<SessionParticipant>) => void;
  /** Force presence to 'present' (used by the auto-present rule). */
  onSetPresence: (presence: 'present' | 'absent') => void;
  /** A write failed after its optimistic update — re-pull the truth. */
  onSyncError: () => void;
}

const EXPLAIN_KEY = 'evolution.explainObservations';
const RECAP_KEY = 'evolution.recapOpen';

/** Long Éveil texts: clamped to a few lines, expandable in place. */
function ExpandableText({
  label, text, moreLabel, lessLabel,
}: { label: string; text: string; moreLabel: string; lessLabel: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="obj-panel-infos">
      <div className="obj-panel-infos-head">
        <span className="obj-panel-measure-label">{label}</span>
        <button type="button" className="obj-panel-infos-toggle" onClick={() => setOpen((v) => !v)}>
          {open ? lessLabel : moreLabel}
        </button>
      </div>
      <div className={`obj-panel-infos-text ${open ? '' : 'obj-panel-infos-text--clamped'}`}>{text}</div>
    </div>
  );
}

/**
 * The participant's table: today's cards laid out large like on the table in
 * the room; under each card its context, horizon and ateliers; the general
 * notes and the défi de la semaine; the objectives recap (mandant, BSP) so
 * nothing needs a second window; the séance précédente pinned; the
 * observations (yes/no card-taking + four A–E dimensions). The back face is
 * the participant's whole evolution.
 */
export function ParticipantEditor({
  session, participant, catalog, onUpdated, onSetPresence, onSyncError,
}: Props) {
  const { t, lang } = useT();
  const dl = lang as DimLang;
  const goals = useObjectives(participant.v2_participant_id, session.scheduled_date, participant.picks.map(p => `${p.topic_id}:${p.horizon}:${p.context}`).join('|'));
  const [notes, setNotes] = useState(participant.context_notes ?? '');
  const [defi, setDefi] = useState(participant.defi ?? '');
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState<HistorySession[] | null>(null);
  const [synth, setSynth] = useState<SynthesisState | null>(null);
  const [objectives, setObjectives] = useState<Objectives | null | 'loading'>('loading');
  const [explain, setExplain] = useState<boolean>(() => localStorage.getItem(EXPLAIN_KEY) !== 'off');
  const [recapOpen, setRecapOpen] = useState<boolean>(() => localStorage.getItem(RECAP_KEY) !== 'closed');
  /** 0 = most recent past session; higher = further back. */
  const [prevIdx, setPrevIdx] = useState(0);
  /** The mat's visible face: today's entry, or the participant's history. */
  const [side, setSide] = useState<'front' | 'back'>('front');
  const [flipPhase, setFlipPhase] = useState<'idle' | 'out' | 'in'>('idle');
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    setNotes(participant.context_notes ?? '');
    setDefi(participant.defi ?? '');
    setSynth(null);
    setPrevIdx(0);
    setSide('front');
    setFlipPhase('idle');
  }, [participant.id]);

  useEffect(() => { localStorage.setItem(EXPLAIN_KEY, explain ? 'on' : 'off'); }, [explain]);
  useEffect(() => { localStorage.setItem(RECAP_KEY, recapOpen ? 'open' : 'closed'); }, [recapOpen]);

  const participantQs = participant.v2_participant_id
    ? `v2_id=${participant.v2_participant_id}`
    : `name=${encodeURIComponent(participant.display_name)}`;

  /** Per-card LLM syntheses for the évolution column: cached server-side by
   *  material hash, so this is cheap unless something was written since. */
  const loadSyntheses = (wait: boolean) => {
    api.get<SynthesisState>(`/api/participants/synthesis?${participantQs}&wait=${wait ? 1 : 0}`)
      .then((r) => {
        setSynth(r);
        if (r.status === 'pending') setTimeout(() => loadSyntheses(true), 1500);
      })
      .catch(() => setSynth({ status: 'error', syntheses: {} }));
  };

  const flip = () => {
    if (flipPhase !== 'idle') return;
    if (side === 'front') { setSynth((prev) => (prev?.status === 'ready' ? prev : { status: 'pending', syntheses: prev?.syntheses ?? {} })); loadSyntheses(true); }
    setFlipPhase('out');
    setTimeout(() => {
      setSide((s) => (s === 'front' ? 'back' : 'front'));
      setFlipPhase('in');
      requestAnimationFrame(() => requestAnimationFrame(() => setFlipPhase('idle')));
    }, 170);
  };

  // Full history: feeds the fil des séances matrix, the trends and the
  // previous-session context. Today's live edits come from `participant`.
  useEffect(() => {
    let alive = true;
    setHistory(null);
    const qs = participant.v2_participant_id
      ? `v2_id=${participant.v2_participant_id}`
      : `name=${encodeURIComponent(participant.display_name)}`;
    api.get<{ sessions: HistorySession[] }>(`/api/participants/history?${qs}`)
      .then((h) => { if (alive) setHistory(h.sessions); })
      .catch(() => { if (alive) setHistory([]); });
    return () => { alive = false; };
  }, [participant.id]);

  // Objectifs (mandant, BSP) and the participant's ateliers, from Éveil.
  useEffect(() => {
    if (!participant.v2_participant_id) {
      setObjectives(null);
      return;
    }
    let alive = true;
    setObjectives('loading');
    api.get<Objectives>(`/api/participants/${participant.v2_participant_id}/objectives?date=${session.scheduled_date}`)
      .then((o) => { if (alive) setObjectives(o); })
      .catch(() => { if (alive) setObjectives(null); });
    return () => { alive = false; };
  }, [participant.id, participant.v2_participant_id]);

  // Recording activity implies the person is here: entering a card or text
  // flips presence to "présent" (an explicit "absent" is never overridden).
  const ensurePresent = () => {
    if (participant.presence === 'expected' || participant.presence === 'added') {
      onSetPresence('present');
    }
  };

  const debounced = (key: string, fn: () => void, ms = 600) => {
    const prev = timers.current.get(key);
    if (prev) clearTimeout(prev);
    timers.current.set(key, setTimeout(() => { timers.current.delete(key); fn(); }, ms));
  };

  const saveNotes = async (html: string) => {
    setNotes(html);
    if (notesHaveContent(html)) ensurePresent();
    onUpdated(participant.id, { context_notes: html });
    await api.patch(`/api/session-participants/${participant.id}/notes`, { context_notes: html });
  };

  const saveDefi = (text: string) => {
    setDefi(text);
    if (text.trim()) ensurePresent();
    onUpdated(participant.id, { defi: text.trim() || null });
    debounced('defi', () => {
      api.patch(`/api/session-participants/${participant.id}/defi`, { defi: text.trim() || null }).catch(onSyncError);
    });
  };

  const setScore = async (dim: ScoreDimension, v: ScoreLetter | null) => {
    onUpdated(participant.id, dim.key === 'prise_cartes'
      ? { [dim.field]: v, takes_cards: v == null ? null : v !== 'A' }
      : { [dim.field]: v });
    try {
      await api.patch(`/api/session-participants/${participant.id}/scores`, { [dim.field]: v });
    } catch {
      onSyncError();
    }
  };

  const setPicks = async (ids: number[]) => {
    const cardById = new Map(catalog.cards.map((c) => [c.id, c]));
    const catById = new Map(catalog.categories.map((c) => [c.id, c]));
    const existing = new Map(participant.picks.map((p) => [p.topic_id, p]));
    const picks: Pick[] = ids.map((id, i) => {
      const card = cardById.get(id);
      const cat = card?.category_id != null ? catById.get(card.category_id) : null;
      const prev = existing.get(id);
      return {
        topic_id: id,
        pick_order: i,
        name: card?.name ?? '',
        pdf_card_number: card?.pdf_card_number ?? null,
        category_color: cat?.color ?? null,
        context: prev?.context ?? null,
        horizon: prev?.horizon ?? null,
        offering_ids: prev?.offering_ids ?? [],
        offering_names: prev?.offering_names ?? [],
      };
    });
    onUpdated(participant.id, { picks });
    if (ids.length > 0) ensurePresent();
    try {
      await api.put(`/api/session-participants/${participant.id}/picks`, { topic_ids: ids });
    } catch {
      onSyncError();
    }
  };

  /** Per-card detail (context / horizon / ateliers): optimistic + debounced for text. */
  const updatePick = (topicId: number, patch: Partial<Pick>, immediate = true) => {
    const picks = participant.picks.map((p) => (p.topic_id === topicId ? { ...p, ...patch } : p));
    onUpdated(participant.id, { picks });
    if (patch.context && patch.context.trim()) ensurePresent();
    const send = () => api.patch(`/api/session-participants/${participant.id}/picks/${topicId}`, patch).catch(onSyncError);
    if (immediate) send(); else debounced(`pick:${topicId}`, send);
  };

  const copyReport = async () => {
    const report = buildParticipantReport({
      session,
      participant: { ...participant, context_notes: notes, defi: defi.trim() || null },
      catalog,
      lang: dl,
      t,
    });
    const ok = await copyText(report);
    if (ok) {
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatShortDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };
  const formatLongDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  };

  // "60 % → 80 % d'ici le 31 juillet 2026" — the latest objectif intermédiaire.
  const formatProgression = (p: NonNullable<Objectives['progression']>) => {
    const from = p.start_pct != null ? `${p.start_pct} % → ` : '';
    const target = p.target_pct != null ? `${from}${p.target_pct} %` : '';
    const by = p.target_date ? ` ${t('editor.objectivesBy')} ${formatShortDate(p.target_date)}` : '';
    return `${target}${by}`.trim() || (p.raw_phrase ?? '');
  };

  const cardById = useMemo(() => new Map(catalog.cards.map((c) => [c.id, c])), [catalog]);
  const catById = useMemo(() => new Map(catalog.categories.map((c) => [c.id, c])), [catalog]);

  // Past sessions strictly before today's, current excluded, oldest → newest.
  const pastSessions = useMemo(
    () => (history ?? []).filter(
      (s) => s.session_id !== session.id && s.scheduled_date <= session.scheduled_date,
    ),
    [history, session.id, session.scheduled_date],
  );

  // The session shown in the "séance précédente" box (stepping back in time).
  const prevShown = pastSessions.length
    ? pastSessions[Math.max(0, pastSessions.length - 1 - prevIdx)]
    : null;
  const canOlder = prevIdx < pastSessions.length - 1;
  const canNewer = prevIdx > 0;

  // Recurrence per topic across past sessions + today's live picks.
  const recurrence = useMemo(() => {
    const m = new Map<number, number>();
    for (const s of pastSessions) {
      for (const p of s.picks) m.set(p.topic_id, (m.get(p.topic_id) ?? 0) + 1);
    }
    for (const p of participant.picks) m.set(p.topic_id, (m.get(p.topic_id) ?? 0) + 1);
    return m;
  }, [pastSessions, participant.picks]);

  const toMatrixPicks = (picks: Pick[]) => picks.map((p) => ({
    topic_id: p.topic_id,
    name: cardById.get(p.topic_id)?.name ?? p.name,
    category_color: p.category_color,
    context: p.context ?? null,
    horizon: p.horizon ?? null,
  }));

  // Fil des séances: past sessions + today's column fed by live picks.
  const matrixSessions: MatrixSession[] = useMemo(() => [
    ...pastSessions.map((s) => ({
      session_id: s.session_id,
      scheduled_date: s.scheduled_date,
      title: s.title,
      picks: toMatrixPicks(s.picks),
    })),
    {
      session_id: session.id,
      scheduled_date: session.scheduled_date,
      title: session.title,
      isToday: true,
      picks: toMatrixPicks(participant.picks),
    },
  ], [pastSessions, participant.picks, session, cardById]);

  // Trends: past observations + today's live letters as the accented endpoint.
  const trendSeries: TrendScores[] = useMemo(() => [
    ...pastSessions.map((s) => ({ ...s.scores, takes_cards: s.takes_cards })),
    {
      prise_cartes: participant.score_prise_cartes,
      takes_cards: participant.takes_cards,
      lien_precedents: participant.score_lien_precedents,
      expression: participant.score_expression,
      responsabilisation: participant.score_responsabilisation,
      defi: participant.score_defi,
    },
  ], [pastSessions, participant]);

  const goalsText = objectives !== 'loading' && objectives?.goals_in_words
    ? htmlToText(objectives.goals_in_words)
    : null;
  const infosText = objectives !== 'loading' && objectives?.informations_complementaires
    ? htmlToText(objectives.informations_complementaires)
    : null;
  const hasObjectives = objectives !== 'loading' && objectives !== null
    && (!!goalsText || !!infosText || objectives.goals.length > 0 || !!objectives.progression);
  const firstName = participant.display_name.split(' ')[0];

  const orderedPicks = [...participant.picks].sort((a, b) => a.pick_order - b.pick_order);

  return (
    <div>
      {/* ── The mat: front = today's entry, back = the participant's history.
             Flipping is the card gesture — same object, two faces. ── */}
      <div className={`mat-flip mat-flip--${flipPhase}`}>
      {side === 'front' ? (
      <CardTable
        catalog={catalog}
        picked={orderedPicks.map((p) => p.topic_id)}
        onChange={setPicks}
        recurrence={recurrence}
        caption={`${t('cards.tableCaption')} — ${formatLongDate(session.scheduled_date)}`}
        disabled={participant.presence === 'absent'}
        onFlip={flip}
        flipLabel={t('editor.flipToHistory')}
        footer={
          <button
            type="button"
            className={`btn btn-accent mat-copy ${copied ? 'btn-copied' : ''}`}
            onClick={copyReport}
            title={t('editor.copyReportHelp')}
          >
            {copied ? <Check size={15} /> : <ClipboardCopy size={15} />}
            {copied ? t('editor.copied') : t('editor.copyReport')}
          </button>
        }
      >
        {/* ── Objectifs & BSP recap: what the mandant, the BSP and the
               intermediate objective say — on the sheet, not in a second window ── */}
        {participant.v2_participant_id && (
          <div className={`mat-recap ${recapOpen ? '' : 'mat-recap--closed'}`}>
            <button type="button" className="mat-recap-head" onClick={() => setRecapOpen((v) => !v)} aria-expanded={recapOpen}>
              <span className="mat-label" style={{ marginBottom: 0 }}>{t('editor.recap')}</span>
              {objectives !== 'loading' && objectives !== null && (objectives.intake_date || objectives.bsp_date) && (
                <span className="obj-panel-source">{t('editor.objectivesSource')} {formatShortDate(objectives.intake_date ?? objectives.bsp_date!)}</span>
              )}
              <ChevronDown size={14} className="mat-recap-chevron" />
            </button>
            {recapOpen && (
              <div className="mat-recap-body">
                {objectives === 'loading' ? (
                  <div className="prevbox-empty">…</div>
                ) : !hasObjectives ? (
                  <div className="prevbox-empty">{t('editor.recapNone')}</div>
                ) : (
                  <>
                    {objectives !== null && (objectives.goals.length > 0 || objectives.progression) && (
                      <div className="mat-recap-row">
                        <span className="mat-recap-k">{t('editor.recapGoals')}</span>
                        <span className="mat-recap-v">
                          {objectives.goals.join(' · ')}
                          {objectives.progression && (
                            <span className="mat-recap-prog" title={objectives.progression.raw_phrase ?? undefined}>
                              {objectives.goals.length > 0 ? ' — ' : ''}{t('editor.recapProgression')} {formatProgression(objectives.progression)}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {goalsText && (
                      <div className="mat-recap-row">
                        <span className="mat-recap-k">Objectifs du dossier Éveil — texte source</span>
                        <span className="mat-recap-v" style={{whiteSpace:'pre-wrap'}}>{goalsText}</span>
                      </div>
                    )}
                    {infosText && (
                      <ExpandableText
                        label={t('editor.objectivesInfos')}
                        text={infosText}
                        moreLabel={t('common.showMore')}
                        lessLabel={t('common.showLess')}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── One context per card: what was said about THIS card, where it
               sits in time, and the ateliers where the theme can be worked ── */}
        {orderedPicks.length > 0 && participant.presence !== 'absent' && (
          <div className="mat-pickmeta">
            <div className="mat-label">{t('editor.pickMeta')}</div>
            <div className="mat-pickmeta-help">{t('editor.pickMetaHelp')}</div>
            {orderedPicks.map((p, i) => {
              const card = cardById.get(p.topic_id);
              const cat = card?.category_id != null ? catById.get(card.category_id) : null;
              return (
                <PickMetaRow
                  key={p.topic_id}
                  spId={participant.id}
                  objective={goals.objectives.find(o => o.topic_id===p.topic_id)}
                  onStatusSaved={goals.refresh}
                  index={i + 1}
                  pick={p}
                  name={card?.name ?? p.name}
                  color={cat?.color ?? p.category_color ?? null}
                  onContext={(text) => updatePick(p.topic_id, { context: text }, false)}
                  onHorizon={(h) => updatePick(p.topic_id, { horizon: h })}
                />
              );
            })}
          </div>
        )}

        <ObjectiveFollowup {...goals} />
        <div className="mat-notes">
          <div className="mat-label">{t('editor.notes')}</div>
          <RichNotes
            sessionParticipantId={participant.id}
            value={notes}
            onSave={saveNotes}
            placeholder={t('editor.notesPlaceholder')}
          />

          {/* Défi de la semaine — concrete, time-bound, in the person's words */}
          <div className="mat-defi">
            <div className="mat-label">{t('editor.defi')}</div>
            <textarea
              className="field-textarea mat-defi-input"
              rows={2}
              value={defi}
              placeholder={t('editor.defiPlaceholder')}
              onChange={(e) => saveDefi(e.target.value)}
              disabled={participant.presence === 'absent'}
            />
          </div>

          {/* Séance précédente — pinned under the notes, steps further back */}
          <div className="prevbox" aria-live="polite">
            <div className="prevbox-head">
              <span className="mat-label prevbox-label">{t('editor.prevSession')}</span>
              {prevShown && (
                <>
                  <span className="prevbox-date">{formatShortDate(prevShown.scheduled_date)}</span>
                  {pastSessions.length > 1 && (
                    <span className="prevbox-nav">
                      <button
                        type="button"
                        className="prevbox-nav-btn"
                        disabled={!canOlder}
                        onClick={() => setPrevIdx((i) => i + 1)}
                        title={t('editor.prevOlder')}
                        aria-label={t('editor.prevOlder')}
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        type="button"
                        className="prevbox-nav-btn"
                        disabled={!canNewer}
                        onClick={() => setPrevIdx((i) => i - 1)}
                        title={t('editor.prevNewer')}
                        aria-label={t('editor.prevNewer')}
                      >
                        <ChevronRight size={14} />
                      </button>
                    </span>
                  )}
                </>
              )}
            </div>
            {history === null ? (
              <div className="prevbox-empty">…</div>
            ) : prevShown === null ? (
              <div className="prevbox-empty">{t('editor.prevSessionNone')}</div>
            ) : (
              <>
                <div className="prevbox-cards">
                  {prevShown.picks.length === 0 && (
                    <span className="prevbox-empty">{t('history.noPicks')}</span>
                  )}
                  {prevShown.picks.map((p) => {
                    const card = cardById.get(p.topic_id);
                    return (
                      <span key={p.topic_id} className="prevcard" title={p.context ?? undefined}>
                        <span className="prevcard-title">{card?.name ?? p.name}</span>
                        {p.horizon && <span className="prevcard-h">{HORIZON_LABELS[p.horizon][dl]}</span>}
                        <span
                          className="prevcard-dot"
                          style={{ background: p.category_color ?? 'var(--ink-3)' }}
                        />
                      </span>
                    );
                  })}
                </div>
                {prevShown.picks.some((p) => p.context) && (
                  <ul className="prevbox-contexts">
                    {prevShown.picks.filter((p) => p.context).map((p) => (
                      <li key={p.topic_id}><strong>{cardById.get(p.topic_id)?.name ?? p.name}</strong> — {p.context}</li>
                    ))}
                  </ul>
                )}
                {prevShown.context_notes && (
                  <div className="prevbox-notes">{htmlToText(prevShown.context_notes)}</div>
                )}
                {prevShown.defi && (
                  <div className="prevbox-defi"><span className="mat-recap-k">{t('editor.prevDefi')}</span> {prevShown.defi}</div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mat-obs">
          <div className="mat-label obs-head">
            <span>{t('editor.observations')}</span>
            <label className="obs-toggle">
              <input
                type="checkbox"
                checked={explain}
                onChange={(e) => setExplain(e.target.checked)}
              />
              <span>{t('score.explainToggle')}</span>
            </label>
          </div>
          <div className="mat-obs-panel">
            <div className="obs-rows">
              {SCORE_DIMENSIONS.map((dim) => (
                <ScoreScale
                  key={dim.key}
                  dimension={dim}
                  value={participant[dim.field]}
                  onChange={(v) => setScore(dim, v)}
                  explain={explain}
                />
              ))}
            </div>
          </div>
        </div>
      </CardTable>
      ) : (
        /* ── The back face: the participant's whole history ── */
        <div className="mat mat--back">
          <div className="mat-head">
            <span className="mat-caption">
              {t('editor.backCaption').replace('{name}', firstName)}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={flip}>
              <FlipHorizontal2 size={14} /> {t('editor.flipToToday')}
            </button>
          </div>

          {/* Objectifs — dans les mots du participant (premier entretien) */}
          <div className="back-block" aria-live="polite">
            <div className="back-block-head">
              <span className="mat-label" style={{ marginBottom: 0 }}>{t('editor.objectivesTitle')}</span>
              {objectives !== 'loading' && objectives !== null && (objectives.intake_date || objectives.bsp_date) && (
                <span className="obj-panel-source">
                  {t('editor.objectivesSource')} {formatShortDate(objectives.intake_date ?? objectives.bsp_date!)}
                </span>
              )}
            </div>
            {objectives === 'loading' ? (
              <div className="prevbox-empty">…</div>
            ) : !hasObjectives ? (
              <div className="prevbox-empty">{t('editor.objectivesNone')}</div>
            ) : (
              <>
                {goalsText && <div className="obj-panel-text">{goalsText}</div>}
                {infosText && (
                  <ExpandableText
                    label={t('editor.objectivesInfos')}
                    text={infosText}
                    moreLabel={t('common.showMore')}
                    lessLabel={t('common.showLess')}
                  />
                )}
                {objectives !== null && (objectives.goals.length > 0 || objectives.progression) && (
                  <div className="obj-panel-measure">
                    <span className="obj-panel-measure-label">{t('editor.objectives')}</span>
                    <span className="obj-panel-measure-text">
                      {objectives.goals.join(' · ')}
                      {objectives.progression && (
                        <span title={objectives.progression.raw_phrase ?? undefined}>
                          {objectives.goals.length > 0 ? ' — ' : ''}{formatProgression(objectives.progression)}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Le fil des séances + les observations, side by side */}
          <div className="back-block">
            <div className="back-block-head">
              <span className="mat-label" style={{ marginBottom: 0 }}>
                {t('editor.evolution').replace('{name}', firstName)}
              </span>
            </div>
            <div className="back-evo">
              <EvolutionTable
                sessions={matrixSessions}
                series={trendSeries}
                categories={catalog.categories}
                syntheses={synth?.status === 'ready' ? synth.syntheses : undefined}
              />
              {synth?.status === 'pending' && <div className="evo-note evo-note--pending">{t('evo.synthesisPending')}</div>}
              {synth?.status === 'ready' && Object.keys(synth.syntheses).length > 0 && <div className="evo-note">{t('evo.synthesisNote')}</div>}
              {synth?.status === 'error' && <div className="evo-note">{t('evo.synthesisError')}</div>}
            </div>
          </div>
        </div>
      )}
      </div>

    </div>
  );
}

/** One picked card's context, horizon and ateliers. */
function PickMetaRow({
  spId, objective, onStatusSaved, index, pick, name, color, onContext, onHorizon,
}: {
  spId: number;
  objective?: Objective;
  onStatusSaved: () => void;
  index: number;
  pick: Pick;
  name: string;
  color: string | null;
  onContext: (text: string) => void;
  onHorizon: (h: Horizon | null) => void;
}) {
  const { t, lang } = useT();
  const dl = lang as DimLang;
  const [text, setText] = useState(pick.context ?? '');
  useEffect(() => { setText(pick.context ?? ''); }, [pick.topic_id]);

  return (
    <div className="pickmeta-row">
      <div className="pickmeta-head">
        <span className="pickmeta-order">{index}</span>
        <span className="pickmeta-dot" style={{ background: color ?? 'var(--ink-3)' }} />
        <span className="pickmeta-name">{name}</span>
        <span className="horizon-seg" role="radiogroup" aria-label={t('editor.horizon')}>
          {HORIZONS.map((h) => (
            <button
              key={h}
              type="button"
              role="radio"
              aria-checked={pick.horizon === h}
              className={`horizon-seg-btn ${pick.horizon === h ? 'horizon-seg-btn--active' : ''}`}
              onClick={() => onHorizon(pick.horizon === h ? null : h)}
              title={HORIZON_LABELS[h][dl]}
            >
              {HORIZON_LABELS[h][dl]}
            </button>
          ))}
        </span>
        <div className="offering-pending">
          <span className="save-indicator">Ateliers — bientôt disponible : correspondances cartes–ateliers en préparation</span>
          {(pick.offering_names ?? []).length>0 && <small>Déjà associés : {pick.offering_names!.join(', ')}</small>}
        </div>
      </div>
      <ObjectiveStatus spId={spId} topicId={pick.topic_id} objective={objective} onSaved={onStatusSaved} />
      <textarea
        className="field-textarea pickmeta-context"
        rows={2}
        value={text}
        placeholder={t('editor.contextPlaceholder').replace('{card}', name)}
        onChange={(e) => { setText(e.target.value); onContext(e.target.value); }}
      />
    </div>
  );
}
